<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import VehicleSelectWithAvatar from "@/components/VehicleSelectWithAvatar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { routeStore } from "@/_store/routeStore";
import { errorUtils } from "@/utils";
import { toSafeString } from "@/utils/strings";

const props = defineProps({
  modalId: { type: String, required: true },
  planId: { type: String, required: true },
  sourceRouteId: { type: String, required: true },
  routeStopId: { type: String, required: true },
});

const emit = defineEmits(["close", "confirm"]);

const modalMoveRouteStop = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const routesStore = routeStore();

const snackbar = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const form = ref({
  targetRouteId: "",
});
const routeOptions = ref([]);
const rules = {
  targetRouteId: {
    required: helpers.withMessage(
      "Debe seleccionar un vehículo de destino.",
      required
    ),
  },
};
const v$ = useVuelidate(rules, form);

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo cargar las rutas del plan.",
    type: "danger",
    color: "white",
  };
}

function extractRoutesFromResponse(response) {
  const source = response?.data || response || {};

  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.routes)) return source.routes;

  return [];
}

function normalizeRouteOption(route, index) {
  const routeId = toSafeString(route?._id);
  if (!routeId) return null;

  const routeName = toSafeString(route?.name) || `Ruta ${index + 1}`;
  return {
    value: routeId,
    label: routeName,
    abbreviation: toSafeString(route?.abbreviation),
    color: toSafeString(route?.route_color_hex),
  };
}

const hasRouteOptions = computed(() => routeOptions.value.length > 0);

async function loadPlanRoutes() {
  const planId = toSafeString(props.planId);
  if (!planId) {
    showError("No se encontro plan_id para cargar rutas.");
    return;
  }

  isLoading.value = true;
  closeSnackbar();
  try {
    const response = await routesStore.getRoutesByPlanId(planId);
    const sourceRouteId = toSafeString(props.sourceRouteId);
    const routes = extractRoutesFromResponse(response);
    routeOptions.value = routes
      .map((route, index) => normalizeRouteOption(route, index))
      .filter((route) => route && route.value !== sourceRouteId);
  } catch (error) {
    showError(
      errorUtils.getErrorMessageHtml(
        error,
        "No se pudo cargar las rutas del plan."
      )
    );
    routeOptions.value = [];
  } finally {
    isLoading.value = false;
  }
}

function handleHide() {
  form.value.targetRouteId = "";
  isSubmitting.value = false;
  v$.value.$reset();
  emit("close");
}

async function confirmMove() {
  if (isLoading.value || isSubmitting.value) return;
  await v$.value.$validate();
  if (v$.value.$errors.length > 0) {
    showError(v$.value.$errors[0].$message);
    return;
  }

  const routeStopId = toSafeString(props.routeStopId);
  if (!routeStopId) {
    showError("No se encontro _id de route_stop para mover la parada.");
    return;
  }

  const targetRouteId = toSafeString(form.value.targetRouteId);

  isSubmitting.value = true;
  closeSnackbar();
  emit("confirm", { targetRouteId });
  modalMoveRouteStop.value?.closeModal();
}

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadPlanRoutes();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalMoveRouteStop"
    width="30rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Cambiar de Ruta</h6>
    </template>
    <template #body>
      <div v-if="!isLoading && !hasRouteOptions" class="row">
        <div class="col">
          <span class="text-sm text-secondary">
            No hay otras rutas disponibles en este plan para mover la parada.
          </span>
        </div>
      </div>
      <div v-else class="row align-items-center">
        <div class="col">
          <vehicle-select-with-avatar
            id="move-route-stop-target-route"
            :model-value="form.targetRouteId"
            :options="routeOptions"
            :error="v$.targetRouteId.$error"
            :filterable="false"
            menu-width="255"
            @update:model-value="(value) => (form.targetRouteId = value)"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSubmitting ? 'Moviendo...' : 'Aceptar'"
        :loading="isSubmitting"
        :disable-button="isLoading || isSubmitting"
        @click="confirmMove"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
